/**
 * Platform-staff → partner impersonation ("open partner console").
 *
 * A super-admin opens a partner's console AS that partner. The backend mints
 * a standalone ~60-min token; here we swap it into `tokenStore` as an overlay
 * (the admin's own tokens stay intact underneath), clear the query cache so
 * no admin-scoped data leaks, and track the active impersonation reactively
 * for the banner. Exiting (or the token expiring) restores the admin session.
 */
import { create } from "zustand";

import { ApiPaths, api, registerImpersonationExpiredHandler } from "./api";
import { queryClient } from "./queryClient";
import { type ImpersonationMeta, tokenStore } from "./tokenStore";

export type ImpersonationTarget = Pick<ImpersonationMeta, "kind" | "id" | "name">;

type State = { current: ImpersonationTarget | null };

const _initial = tokenStore.getImpersonation();
export const useImpersonation = create<State>(() => ({
  current: _initial
    ? { kind: _initial.kind, id: _initial.id, name: _initial.name }
    : null,
}));

type ImpersonateResponse = {
  access: string;
  partner_kind: "reseller" | "commercial";
  partner_id: string;
  partner_name: string;
};

/** Open the partner console as `(kind, id)`. Made AS the admin (the current
 *  token), then swaps to the returned partner token. */
export async function startImpersonation(
  kind: "reseller" | "commercial",
  id: string,
): Promise<void> {
  const res = await api.post<ImpersonateResponse>(
    ApiPaths.platformPartnerImpersonate(kind, id),
  );
  const meta: ImpersonationMeta = {
    access: res.data.access,
    kind: res.data.partner_kind,
    id: res.data.partner_id,
    name: res.data.partner_name,
  };
  tokenStore.startImpersonation(meta);
  queryClient.clear(); // drop admin-scoped cached data before the partner view loads
  useImpersonation.setState({
    current: { kind: meta.kind, id: meta.id, name: meta.name },
  });
}

/** Exit impersonation → the admin session (untouched under the overlay) is
 *  live again. */
export function stopImpersonation(): void {
  tokenStore.stopImpersonation();
  queryClient.clear(); // drop partner-scoped cached data
  useImpersonation.setState({ current: null });
}

// A 401 on the partner token (expiry/revocation) exits impersonation. Hard-nav
// to the platform partners page so the app re-bootstraps cleanly on the admin
// session (this is a rare ~60-min-expiry edge, so a full reload is fine).
registerImpersonationExpiredHandler(() => {
  stopImpersonation();
  try {
    globalThis.location?.assign("/platform/partners");
  } catch {
    /* no window (tests) */
  }
});
